import { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuthContext } from '../context/AuthContext';
import { toast } from 'react-hot-toast';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

/**
 * Format phone number as (XXX) XXX-XXXX
 */
const formatPhoneNumber = (value) => {
  if (!value) return '';
  // Only keep digits
  const phoneNumber = value.replace(/\D/g, '');
  // Take up to 10 digits
  const digits = phoneNumber.slice(0, 10);
  
  // Format based on number of digits
  if (digits.length <= 3) {
    return digits;
  } else if (digits.length <= 6) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  } else {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
};

/**
 * Clean phone number to just digits
 */
const cleanPhoneNumber = (value) => {
  return value.replace(/\D/g, '');
};

/**
 * Validate phone number
 */
const validatePhoneNumber = (value) => {
  const cleaned = cleanPhoneNumber(value);
  // Only check for exactly 10 digits, ignore formatting
  return cleaned.length === 10;
};

/**
 * Validate password
 */
const validatePassword = (password) => {
  const errors = [];
  if (password.length < 6) {
    errors.push('Password must be at least 6 characters');
  }
  if (!/[a-z]/.test(password)) {
    errors.push('Password must contain at least one lowercase letter');
  }
  if (!/[!@#$%^&*]/.test(password)) {
    errors.push('Password must contain at least one special character (!@#$%^&*)');
  }
  return errors;
};

/**
 * Validate date of birth for realistic age range
 */
const validateDateOfBirth = (dateString) => {
  if (!dateString) return 'Date of birth is required';
  
  const dob = new Date(dateString);
  const today = new Date();
  
  // Check if date is valid
  if (isNaN(dob.getTime())) {
    return 'Please enter a valid date';
  }
  
  // Check if date is in the future
  if (dob > today) {
    return 'Date of birth cannot be in the future';
  }
  
  // Calculate age
  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();
  
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
    age--;
  }
  
  // Check minimum age (18)
  if (age < 18) {
    return 'You must be at least 18 years old to register';
  }
  
  // Check maximum age (115)
  if (age > 115) {
    return 'Please enter a valid date of birth';
  }
  
  return null;
};

/**
 * Get minimum date for date picker (115 years ago from today)
 */
const getMinDate = () => {
  const today = new Date();
  const minDate = new Date(today.getFullYear() - 115, today.getMonth(), today.getDate());
  return minDate.toISOString().split('T')[0];
};

/**
 * Get maximum date for date picker (18 years ago from today)
 */
const getMaxDate = () => {
  const today = new Date();
  const maxDate = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate());
  return maxDate.toISOString().split('T')[0];
};

/**
 * Register Component
 * 🔐 Handles new user registration form and submission
 */
const Register = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { register, error } = useAuthContext();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    dateOfBirth: '',
    password: '',
    confirmPassword: '',
    invitationCode: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [dateError, setDateError] = useState('');
  const [hasInvitation, setHasInvitation] = useState(false);
  const [inviteMode, setInviteMode] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Check for invitation data on component mount
  useEffect(() => {
    console.log('Register component mounted, checking for invitation data...');
    
    // First check for partner invite code in URL
    const params = new URLSearchParams(location.search);
    const partnerInviteCode = params.get('invite');
    
    if (partnerInviteCode) {
      console.log('🎟️ Partner invitation code detected in URL:', partnerInviteCode);
      setFormData(prevData => ({
        ...prevData,
        invitationCode: partnerInviteCode
      }));
      setHasInvitation(true);
      setInviteMode(true);
      // No need to continue with regular invitation check
      return;
    }
    
    // Continue with regular invitation check from session storage
    const invitationData = sessionStorage.getItem('invitationData');
    
    console.log('Invitation data from session storage:', invitationData);
    
    if (!invitationData) {
      console.log('No invitation data found in session storage, redirecting to verification page...');
      // Redirect to invitation verification if no invitation code is present
      toast.error('Please verify your invitation code first');
      navigate('/verify-invitation', { replace: true });
      return;
    }
    
    try {
      const parsedData = JSON.parse(invitationData);
      console.log('Successfully parsed invitation data:', parsedData);
      
      if (!parsedData || !parsedData.code) {
        throw new Error('Invalid invitation data format');
      }
      
      setFormData(prevData => ({
        ...prevData,
        email: parsedData.email || '',
        invitationCode: parsedData.code || ''
      }));
      
      console.log('Invitation data applied to form');
      setHasInvitation(true);
    } catch (error) {
      console.error('Error parsing invitation data:', error);
      toast.error('Invalid invitation data. Please try again.');
      navigate('/verify-invitation', { replace: true });
    }
  }, [navigate, location]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'phoneNumber') {
      const formattedPhone = formatPhoneNumber(value);
      setFormData({
        ...formData,
        [name]: formattedPhone,
      });
      
      // Clear phone error when user starts typing
      if (phoneError) {
        setPhoneError('');
      }
    } else if (name === 'dateOfBirth') {
      setFormData({
        ...formData,
        [name]: value,
      });
      
      // Validate date of birth
      const dobError = validateDateOfBirth(value);
      setDateError(dobError || '');
    } else if (name === 'password') {
      setFormData({
        ...formData,
        [name]: value,
      });
      
      // Show password validation errors when typing stops but don't block input
      if (value.length > 0) {
        const passwordErrors = validatePassword(value);
        if (passwordErrors.length > 0) {
          setPasswordError(passwordErrors.join('\n'));
        } else {
          setPasswordError('');
        }
      } else {
        setPasswordError('');
      }
    } else if (name === 'confirmPassword') {
      setFormData({
        ...formData,
        [name]: value,
      });
      
      // Check if passwords match
      if (formData.password && value && formData.password !== value) {
        setPasswordError('Passwords do not match');
      } else {
        // Revalidate password requirements if they match
        if (formData.password.length > 0) {
          const passwordErrors = validatePassword(formData.password);
          if (passwordErrors.length > 0) {
            setPasswordError(passwordErrors.join('\n'));
          } else {
            setPasswordError('');
          }
        }
      }
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    
    // Validate last name length
    if (formData.lastName.length < 2 || formData.lastName.length > 50) {
      toast.error('Last name must be between 2 and 50 characters');
      return;
    }
    
    // Validate date of birth
    const dobError = validateDateOfBirth(formData.dateOfBirth);
    if (dobError) {
      setDateError(dobError);
      return;
    }
    
    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    // Validate password strength
    const passwordErrors = validatePassword(formData.password);
    if (passwordErrors.length > 0) {
      setPasswordError(passwordErrors.join('\n'));
      return;
    }

    // Validate phone number
    if (!validatePhoneNumber(formData.phoneNumber)) {
      setPhoneError('Please enter a valid 10-digit phone number');
      return;
    }

    // Ensure invitation code is present
    if (!formData.invitationCode) {
      if (inviteMode) {
        toast.error('Missing partner invitation code. Please retry the invitation link.');
        return;
      } else {
        toast.error('Missing invitation code. Please restart the signup process.');
        navigate('/verify-invitation', { replace: true });
        return;
      }
    }

    try {
      setIsSubmitting(true);
      const { confirmPassword, ...registrationData } = formData;
      
      // Clean up phone number before sending to server
      const cleanedData = {
        ...registrationData,
        phoneNumber: cleanPhoneNumber(registrationData.phoneNumber),
        // Ensure date is in ISO format for the server
        dateOfBirth: new Date(registrationData.dateOfBirth).toISOString(),
      };
      
      console.log('Registration data being sent:', cleanedData);
      const result = await register(cleanedData);
      
      // Success notification
      toast.success('Account created successfully!');
      
      // If this was a partner invitation, store it for post-login processing
      if (inviteMode) {
        sessionStorage.setItem('partnerInviteCode', formData.invitationCode);
      }
      
      // Clear invitation data from session storage
      sessionStorage.removeItem('invitationData');
      
      // Small delay to ensure user data is properly loaded after registration
      setTimeout(() => {
        // For partner invites, redirect to dashboard with flag to open partner modal
        if (inviteMode) {
          navigate('/dashboard?partner-invite=true', { replace: true });
        } else {
          // ONBOARDING DISABLED - redirect to dashboard directly
          console.log('🚀 Redirecting to dashboard (onboarding disabled)');
          navigate('/dashboard', { 
            replace: true
          });
        }
      }, 100); // Small delay to ensure auth context is updated
    } catch (error) {
      console.error('Registration error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Don't render the form if we haven't confirmed invitation yet
  if (!hasInvitation && !inviteMode) {
    return <div>Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#8B5CF6] via-[#7C3AED] to-[#6D28D9] flex items-center justify-center p-4">
      <div className="relative z-10 bg-white bg-opacity-95 backdrop-blur-sm p-8 rounded-3xl shadow-xl w-full max-w-md mx-4 my-8">
        <h2 className="text-2xl font-semibold text-center mb-2 text-[#6C5DD3]">Create account</h2>
        <div className="text-center mb-8">
          <span className="text-gray-600 text-sm">Already have an account? </span>
          <Link to="/login" className="text-[#6C5DD3] hover:text-[#5849B6] text-sm font-medium">
            Sign in
          </Link>
        </div>

        {(error || passwordError || phoneError || dateError) && (
          <div className="mb-6 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm">
            {error && <div>{error}</div>}
            {phoneError && <div>{phoneError}</div>}
            {dateError && <div>{dateError}</div>}
            {passwordError && passwordError.split('\n').map((err, index) => (
              <div key={index} className="my-1">{err}</div>
            ))}
          </div>
        )}

        {/* Password requirements help text */}
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 text-blue-700 rounded-lg text-sm">
          <p className="font-medium mb-1">Password requirements:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>At least 6 characters long</li>
            <li>Contains at least one lowercase letter (a-z)</li>
            <li>Contains at least one special character (!@#$%^&*)</li>
          </ul>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="block text-gray-600 text-sm" htmlFor="firstName">
                First Name
              </label>
              <input
                id="firstName"
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#6C5DD3] focus:border-transparent text-gray-900 text-sm"
                placeholder="Enter first name"
                required
                disabled={isSubmitting}
              />
            </div>
            <div className="space-y-2">
              <label className="block text-gray-600 text-sm" htmlFor="lastName">
                Last Name
              </label>
              <input
                id="lastName"
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#6C5DD3] focus:border-transparent text-gray-900 text-sm"
                placeholder="Enter last name"
                required
                disabled={isSubmitting}
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-gray-600 text-sm" htmlFor="email">
              Email address
            </label>
            <input
              id="email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#6C5DD3] focus:border-transparent text-gray-900 text-sm"
              placeholder="Enter your email"
              required
              disabled={isSubmitting}
            />
          </div>

          <div className="space-y-2">
            <label className="block text-gray-600 text-sm" htmlFor="phoneNumber">
              Phone Number
            </label>
            <input
              id="phoneNumber"
              type="tel"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#6C5DD3] focus:border-transparent text-gray-900 text-sm"
              placeholder="(XXX) XXX-XXXX"
              required
              disabled={isSubmitting}
            />
          </div>

          <div className="space-y-2">
            <label className="block text-gray-600 text-sm" htmlFor="dateOfBirth">
              Date of Birth
            </label>
            <input
              id="dateOfBirth"
              type="date"
              name="dateOfBirth"
              value={formData.dateOfBirth}
              onChange={handleChange}
              min={getMinDate()}
              max={getMaxDate()}
              className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#6C5DD3] focus:border-transparent text-gray-900 text-sm"
              required
              disabled={isSubmitting}
            />
          </div>

          <div className="space-y-2">
            <label className="block text-gray-600 text-sm" htmlFor="password">
              Password
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                name="password"
                value={formData.password}
                onChange={handleChange}
                className="w-full px-4 py-3 pr-12 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#6C5DD3] focus:border-transparent text-gray-900 text-sm"
                placeholder="Create a password"
                required
                disabled={isSubmitting}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowPassword(!showPassword)}
                disabled={isSubmitting}
              >
                {showPassword ? (
                  <EyeSlashIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                ) : (
                  <EyeIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                )}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-gray-600 text-sm" htmlFor="confirmPassword">
              Confirm Password
            </label>
            <div className="relative">
              <input
                id="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                className="w-full px-4 py-3 pr-12 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#6C5DD3] focus:border-transparent text-gray-900 text-sm"
                placeholder="Confirm your password"
                required
                disabled={isSubmitting}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                disabled={isSubmitting}
              >
                {showConfirmPassword ? (
                  <EyeSlashIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                ) : (
                  <EyeIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                )}
              </button>
            </div>
          </div>

          {/* Hidden field for invitation code */}
          <input
            type="hidden"
            name="invitationCode"
            value={formData.invitationCode}
          />

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full py-3 px-4 rounded-xl text-white font-medium text-sm transition duration-200 ease-in-out disabled:opacity-50 bg-gradient-to-r from-[#6C5DD3] to-[#8247E5] hover:opacity-90"
          >
            {isSubmitting ? 'Creating account...' : 'Create account'}
          </button>
        </form>
      </div>

      {/* Footer */}
      <div className="absolute bottom-4 left-0 right-0 text-center text-white text-xs opacity-70">
        © 2024 O Industries LLC All rights reserved.
      </div>
    </div>
  );
};

export default Register; 